import { memo, useCallback } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';

import type {
  OrganizationFilters as OrganizationFiltersType,
  OrganizationSortableField,
  OrganizationSortOrder,
} from '@/types';

import styles from './OrganizationFilters.module.scss';

interface OrganizationFiltersProps {
  readonly filters: OrganizationFiltersType;
  readonly onFiltersChange: (filters: OrganizationFiltersType) => void;
  readonly loading?: boolean;
}

const SORT_OPTIONS: readonly {
  value: OrganizationSortableField;
  label: string;
}[] = [
  { value: 'facility_count', label: 'Location Count' },
  { value: 'avg_confidence', label: 'Confidence' },
  { value: 'name', label: 'Name' },
];

const DEFAULT_FILTERS: OrganizationFiltersType = {
  min_confidence: 0,
  min_facility_count: 1,
  sort_by: 'facility_count',
  sort_order: 'desc',
};

export const OrganizationFilters = memo<OrganizationFiltersProps>(
  function OrganizationFilters({ filters, onFiltersChange, loading }) {
    const handleConfidenceChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFiltersChange({
          ...filters,
          min_confidence: parseInt(e.target.value, 10),
        });
      },
      [filters, onFiltersChange]
    );

    const handleMinFacilitiesChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value) && value >= 0) {
          onFiltersChange({
            ...filters,
            min_facility_count: value,
          });
        }
      },
      [filters, onFiltersChange]
    );

    const handleSortByChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        const sortBy = e.target.value as OrganizationSortableField;
        // Default sort order based on field
        const defaultOrder: OrganizationSortOrder =
          sortBy === 'name' ? 'asc' : 'desc';
        onFiltersChange({
          ...filters,
          sort_by: sortBy,
          sort_order: defaultOrder,
        });
      },
      [filters, onFiltersChange]
    );

    const handleSortOrderChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        onFiltersChange({
          ...filters,
          sort_order: e.target.value as OrganizationSortOrder,
        });
      },
      [filters, onFiltersChange]
    );

    const handleClear = useCallback(() => {
      onFiltersChange(DEFAULT_FILTERS);
    }, [onFiltersChange]);

    const hasActiveFilters =
      filters.min_confidence !== DEFAULT_FILTERS.min_confidence ||
      filters.min_facility_count !== DEFAULT_FILTERS.min_facility_count ||
      filters.sort_by !== DEFAULT_FILTERS.sort_by ||
      filters.sort_order !== DEFAULT_FILTERS.sort_order;

    return (
      <Card className={styles.filtersCard}>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label className={styles.label}>
                  Min Confidence: {filters.min_confidence ?? 70}%
                </Form.Label>
                <Form.Range
                  min={0}
                  max={100}
                  step={5}
                  value={filters.min_confidence ?? 70}
                  onChange={handleConfidenceChange}
                  disabled={loading}
                  className={styles.slider}
                />
              </Form.Group>
            </Col>

            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className={styles.label}>Min Locations</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={filters.min_facility_count ?? 1}
                  onChange={handleMinFacilitiesChange}
                  disabled={loading}
                  className={styles.input}
                />
              </Form.Group>
            </Col>

            <Col xs={6} md={3}>
              <Form.Group>
                <Form.Label className={styles.label}>Sort By</Form.Label>
                <Form.Select
                  value={filters.sort_by ?? 'facility_count'}
                  onChange={handleSortByChange}
                  disabled={loading}
                  className={styles.select}
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={6} md={2}>
              <Form.Group>
                <Form.Label className={styles.label}>Order</Form.Label>
                <Form.Select
                  value={filters.sort_order ?? 'desc'}
                  onChange={handleSortOrderChange}
                  disabled={loading}
                  className={styles.select}
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={6} md={2} className="d-flex justify-content-end">
              {hasActiveFilters && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClear}
                  disabled={loading}
                  className={styles.clearButton}
                >
                  <i className="bi bi-x-circle me-1" />
                  Reset
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  }
);
